// PreviewBooking.js
import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  SafeAreaView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import { Linking, Platform } from "react-native";

// --- Helper: followers string to 10k-range (handles k/m/b and plain numbers) ---
const parseFollowerRange = (followersString) => {
  if (!followersString) return "0–10k";

  const s = String(followersString).trim().toLowerCase();
  const match = s.match(/([\d.,]+)\s*([kmb]?)/i);
  if (!match) return "0–10k";

  const base = parseFloat(match[1].replace(/,/g, ""));
  if (isNaN(base)) return "0–10k";

  const suffix = match[2];
  const multiplier =
    suffix === "k"
      ? 1000
      : suffix === "m"
      ? 1000000
      : suffix === "b"
      ? 1000000000
      : 1;

  const num = Math.round(base * multiplier);

  const ranges = [];
  for (let i = 0; i < 1000000; i += 10000) {
    ranges.push({
      max: i + 10000,
      label: `${i / 1000}k–${(i + 10000) / 1000}k`,
    });
  }
  const range = ranges.find((r) => num <= r.max);
  return range ? range.label : "1M+";
};

// --- Format date/time ---
const formatNiceDateTime = (iso) => {
  if (!iso) return "a date soon";
  const d = new Date(iso);
  const day = d.getDate();
  const month = d.toLocaleString(undefined, { month: "long" });
  const hours = d.getHours() % 12 || 12;
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  const ord = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  return `${day}${ord(day)} ${month} ${hours}:${minutes}${ampm}`;
};

// --- Helper: read query params on web (PreviewBooking?Promoter=...&CollabID=...) ---
const readWebQueryParams = () => {
  try {
    if (typeof window === "undefined") return {};
    const search = window.location.search || "";
    const sp = new URLSearchParams(search);
    const out = {};
    for (const [k, v] of sp.entries()) out[k] = v;
    return out;
  } catch (e) {
    return {};
  }
};

const pickFirstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

// ---------------- SIDE NAV (replicated styling) ----------------
const DesktopSideNav = ({ navigation, activeKey = "" }) => {
  const [userData, setUserData] = useState({});
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState("none"); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === "subscriber";

  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/GetSessionData.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => setUserData(d || {}))
      .catch((e) => console.error("SideNav session fetch error:", e));
  }, []);

  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php", {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data?.authenticated === "boolean"
            ? data.authenticated
            : data?.status === "active";
        const mode = data?.auth_mode
          ? data.auth_mode
          : data?.status === "active"
          ? "subscriber"
          : "none";

        setIsAuthed(!!authed);
        setAuthMode(mode);
      })
      .catch((err) => console.error("SideNav auth fetch error:", err))
      .finally(() => setAuthChecked(true));
  }, []);

  const openInSameTab = (url) => {
    if (typeof window !== "undefined") {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  const NavItem = ({ itemKey, label, icon, onPress, locked }) => {
    const isActive = activeKey && activeKey === itemKey;
    return (
      <TouchableOpacity
        style={[snav.navItem, isActive && snav.navItemActive]}
        onPress={onPress}
        activeOpacity={0.85}
      >
        <View style={snav.navItemRow}>
          <View style={snav.navItemMainLabel}>
            <Ionicons name={icon} size={20} color="#f5f5f5" style={snav.navIcon} />
            <Text style={snav.navText}>{label}</Text>
          </View>

          {locked ? (
            <Ionicons
              name="lock-closed-outline"
              size={16}
              color="#FDF05D"
              style={snav.lockIcon}
            />
          ) : null}
        </View>
      </TouchableOpacity>
    );
  };

  const lockedCalendar = authChecked && !isAuthed;
  const lockedActivity = authChecked && !isAuthed;

  return (
    <View style={snav.sideNav}>
      <LinearGradient colors={["#050509", "#050505", "#000000"]} style={snav.sideNavInner}>
        <LinearGradient
          colors={["rgba(253,240,93,0.08)", "rgba(0,0,0,0)"]}
          style={snav.sideNavGlow}
          start={{ x: 0.1, y: 0 }}
          end={{ x: 1, y: 1 }}
        />

        <View style={snav.sideNavHeader}>
          <Image
            source={{ uri: "https://collabbayadmin.com/CollabbayLogoDark.png" }}
            style={snav.sideNavLogoImage}
            resizeMode="contain"
          />
          <View>
            <Text style={snav.logoWordmark}>
              <Text style={{ fontWeight: "300" }}>collab</Text>
              bay
              <Text style={snav.logoDot}>.</Text>
            </Text>
            <Text style={snav.logoDesktopTag}>DESKTOP</Text>
          </View>
        </View>

        <View style={snav.sideNavSection}>
          <LinearGradient
            colors={["#111111", "#2b2b2b"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={snav.usernamePill}
          >
            <Text numberOfLines={1} ellipsizeMode="tail" style={snav.usernamePillText}>
              {userData?.Username ? ` ${userData.Username}` : "Welcome Back"}
            </Text>
          </LinearGradient>

          <NavItem
            itemKey="MyPanel"
            label="Home"
            icon="home-outline"
            onPress={() => navigation.navigate("MyPanel")}
          />
          <NavItem
            itemKey="MyOffers"
            label="My Offers"
            icon="pricetags-outline"
            onPress={() => navigation.navigate("MyOffers")}
          />
          <NavItem
            itemKey="MyCalendar"
            label="My Calendar"
            icon="calendar-outline"
            locked={lockedCalendar}
            onPress={() => {
              if (lockedCalendar) openInSameTab("https://collabbayadmin.com/subscribe");
              else navigation.navigate("MyCalendar");
            }}
          />
          <NavItem
            itemKey="BrowseCreators"
            label="Browse Creators"
            icon="people-outline"
            onPress={() => navigation.navigate("BrowseCreators")}
          />
          <NavItem
            itemKey="MyActivityFeed"
            label="Activity"
            icon="chatbubbles-outline"
            locked={lockedActivity}
            onPress={() => {
              if (lockedActivity) openInSameTab("https://collabbayadmin.com/subscribe");
              else navigation.navigate("MyActivityFeed");
            }}
          />
          <NavItem
            itemKey="BusinessAccountPage"
            label="Account"
            icon="person-circle-outline"
            onPress={() => navigation.navigate("BusinessAccountPage")}
          />
        </View>

        <View style={snav.sideNavSection}>
          <TouchableOpacity
            style={snav.peerMapCard}
            activeOpacity={0.9}
            onPress={() => openInSameTab("/venues")}
          >
            <LinearGradient
              colors={["rgba(255,255,255,0.08)", "rgba(0,0,0,0.4)"]}
              style={snav.peerMapInner}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            >
              <View style={snav.peerMapContent}>
                <Ionicons name="map-outline" size={18} color="#FDF05D" style={{ marginRight: 8 }} />
                <View style={{ flex: 1 }}>
                  <Text style={snav.peerMapTitle}>Peer Map</Text>
                  <Text style={snav.peerMapSubtitle}>View 100+ venues on Collabbay</Text>
                </View>
              </View>
            </LinearGradient>
          </TouchableOpacity>

          {!isSubscriber ? (
            <TouchableOpacity
              style={snav.subscribeCard}
              activeOpacity={0.9}
              onPress={() => openInSameTab("https://collabbayadmin.com/subscribe")}
            >
              <LinearGradient
                colors={["#FDF05D", "#fbbf24"]}
                style={snav.subscribeGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
              >
                <View style={snav.subscribeContent}>
                  <Ionicons name="rocket-outline" size={20} color="#000" style={{ marginRight: 8 }} />
                  <View style={{ flex: 1 }}>
                    <Text style={snav.subscribeTitle}>Subscribe & unlock</Text>
                    <Text style={snav.subscribeSubtitle}>Unlock Activity & Calendar, plus more.</Text>
                  </View>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={snav.sideNavBottom}>
          <TouchableOpacity
            style={snav.bottomLink}
            onPress={() => openInSameTab("https://collabbayadmin.com/FAQs")}
          >
            <Ionicons name="help-circle-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
            <View>
              <Text style={snav.bottomLinkText}>FAQs</Text>
              <Text style={snav.bottomLinkCaption}>Common questions answered</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity style={snav.bottomLink} onPress={() => Linking.openURL("mailto:info@collab-bay.com")}>
            <Ionicons name="headset-outline" size={18} color="#f5f5f5" style={{ marginRight: 8 }} />
            <View>
              <Text style={snav.bottomLinkText}>Help</Text>
              <Text style={snav.bottomLinkCaption}>We&apos;re here 24/7</Text>
            </View>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
};

// ---------------- MAIN SCREEN (desktop shell + your existing content) ----------------
const CreatorPreview = () => {
  const navigation = useNavigation();
  const route = useRoute();

  // ✅ merge route.params + web query params (so PreviewBooking?Promoter=... works)
  const webQuery = useMemo(() => (Platform.OS === "web" ? readWebQueryParams() : {}), []);

  const Promoter = pickFirstNonEmpty(
    route?.params?.Promoter,
    route?.params?.promoter,
    route?.params?.Username,
    route?.params?.creator,
    webQuery?.Promoter,
    webQuery?.promoter,
    webQuery?.Username,
    webQuery?.creator
  );

  const CollabID = pickFirstNonEmpty(
    route?.params?.CollabID,
    route?.params?.collabid,
    route?.params?.collabID,
    webQuery?.CollabID,
    webQuery?.collabid,
    webQuery?.collabID
  );

  // ✅ ensure downstream code sees params even if deep link came via query string
  useEffect(() => {
    const current = route?.params || {};
    const needsPromoter = !current?.Promoter && !!Promoter;
    const needsCollabID = !current?.CollabID && !!CollabID;

    if (needsPromoter || needsCollabID) {
      navigation.setParams({
        ...current,
        Promoter: Promoter || current.Promoter,
        CollabID: CollabID || current.CollabID,
      });
    }
  }, [navigation, route?.params, Promoter, CollabID]);

const [creator, setCreator] = useState(null);
const [booking, setBooking] = useState(null);
const [loading, setLoading] = useState(true);
const [bookingLoading, setBookingLoading] = useState(true);
const [countdown, setCountdown] = useState("");
const [creatorRevealLoading, setCreatorRevealLoading] = useState(false);
const [creatorRevealError, setCreatorRevealError] = useState("");

  // Fetch creator info
  useEffect(() => {
    if (!Promoter) {
      setLoading(false);
      return;
    }
    const url = `https://collabbayadmin.com/APIControllers/GetCreatorPreviewData.php?creator=${encodeURIComponent(
      Promoter
    )}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => setCreator(data))
      .catch((err) => console.error("Preview fetch error:", err))
      .finally(() => setLoading(false));
  }, [Promoter]);

  // Fetch booking info
  useEffect(() => {
    if (!CollabID) {
      setBookingLoading(false);
      return;
    }
    const url = `https://collabbayadmin.com/APIControllers/GetPreviewBookingDetails.php?collabid=${encodeURIComponent(
      CollabID
    )}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => setBooking(data))
      .catch((err) => console.error("Booking preview fetch error:", err))
      .finally(() => setBookingLoading(false));
  }, [CollabID]);

  // Countdown timer (live updating)
  useEffect(() => {
    if (!booking?.CreditAvailableStart) return;
    const target = new Date(booking.CreditAvailableStart).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        setCountdown("0d 0h 0m 0s");
        clearInterval(interval);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, [booking?.CreditAvailableStart]);

  const followerRange = useMemo(
    () => parseFollowerRange(creator?.PlatformFollowers),
    [creator?.PlatformFollowers]
  );

  const niceWhen = bookingLoading ? "..." : formatNiceDateTime(booking?.CreditAvailableStart);

  const firstLetter = Promoter ? Promoter.charAt(0) : "C";
  const blurred = "•••••••";
  const turnaround = booking?.TurnAroundDays || "a few";






const canClickRevealCreator = booking?.CreatorInstagramEligible === true;

const handleRevealCreator = async () => {
  if (!CollabID || creatorRevealLoading) return;

  setCreatorRevealLoading(true);
  setCreatorRevealError("");

  try {
    const url = `https://collabbayadmin.com/APIControllers/GetPreviewBookingDetails.php?collabid=${encodeURIComponent(
      CollabID
    )}&revealcreator=1`;

    const res = await fetch(url);
    const data = await res.json();

    if (data?.CreatorInstagramAllowed === true && data?.CreatorInstagramUrl) {
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.open(data.CreatorInstagramUrl, "_blank", "noopener,noreferrer");
      } else {
        Linking.openURL(data.CreatorInstagramUrl);
      }
    } else {
      setCreatorRevealError(
        data?.CreatorInstagramMessage || "Creator preview is not available."
      );
    }
  } catch (err) {
    console.error("Reveal creator Instagram error:", err);
    setCreatorRevealError("Could not open creator preview. Please try again.");
  } finally {
    setCreatorRevealLoading(false);
  }
};


  // Loading shell (desktop)
  if (loading) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeAreaShell}>
          <View style={styles.shellRow}>
            <DesktopSideNav navigation={navigation} activeKey="" />
            <View style={styles.mainArea}>
              <View style={styles.loadingCenter}>
                <ActivityIndicator size="large" color="#111827" />
                <Text style={styles.loadingText}>Loading creator preview…</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  // Not found shell (desktop) - ✅ no red text
  if (!creator || creator.error) {
    return (
      <View style={styles.root}>
        <SafeAreaView style={styles.safeAreaShell}>
          <View style={styles.shellRow}>
            <DesktopSideNav navigation={navigation} activeKey="" />
            <View style={styles.mainArea}>
              <View style={styles.loadingCenter}>
                <Text style={styles.errorText}>Creator unavailable.</Text>
              </View>
            </View>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safeAreaShell}>
        <View style={styles.shellRow}>
          {/* SIDE NAV */}
          <DesktopSideNav navigation={navigation} activeKey="" />

          {/* MAIN AREA */}
          <View style={styles.mainArea}>
            <ScrollView
              contentContainerStyle={styles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.pageInner}>
                {/* 🔙 Back (same as your content, just placed in desktop main area) */}
                <View style={styles.backContainer}>
                  <TouchableOpacity
                    onPress={() => navigation.goBack()}
                    style={styles.backButton}
                    activeOpacity={0.85}
                  >
                    <Ionicons name="arrow-back" size={20} color="#000" />
                    <Text style={styles.backButtonText}>Back</Text>
                  </TouchableOpacity>
                </View>

                {/* 🧾 Main Card (same content, desktop-friendly layout) */}
                <View style={styles.card}>
                  <View style={styles.cardRow}>
                    {/* LEFT: Image */}
                    <View style={styles.leftCol}>
                      <View style={styles.imageWrapper}>
                        {creator?.ProfileImage ? (
                          <Image
                            source={{ uri: creator.ProfileImage }}
                            style={styles.profileImage}
                          />
                        ) : (
                          <View style={styles.imageFallback}>
                            <Text style={styles.imageFallbackText}>{firstLetter}</Text>
                          </View>
                        )}

                        {/* Followers bottom-left */}
                        <View style={styles.overlayLeft}>
                          <View style={styles.overlayBox}>
                            <Text style={styles.overlayBigText}>{followerRange}</Text>
                            <Text style={styles.overlaySmallText}>Followers</Text>
                          </View>
                        </View>

                        {/* Age bottom-right */}
                        {creator.Age ? (
                          <View style={styles.overlayRight}>
                            <View style={styles.overlayBox}>
                              <Text style={styles.overlayBigText}>{creator.Age}</Text>
                              <Text style={styles.overlaySmallText}>Age</Text>
                            </View>
                          </View>
                        ) : null}
                      </View>
                    </View>

                    {/* RIGHT: Details (same elements as your mobile view) */}
                    <View style={styles.rightCol}>
{/* @Username */}
<View style={styles.middleSection}>
  <View style={styles.creatorNameRow}>
    <Text style={styles.username}>
      @{firstLetter}
      <Text style={styles.blurredText}>{blurred}</Text>
    </Text>

    <Ionicons
      name="checkmark-circle"
      size={18}
      color="#1DA1F2"
      style={styles.verifiedTick}
    />
  </View>

  {canClickRevealCreator ? (
    <TouchableOpacity
      style={styles.creatorRevealButton}
      onPress={handleRevealCreator}
      activeOpacity={0.88}
      disabled={creatorRevealLoading}
    >
      <Ionicons name="eye-outline" size={17} color="#fff" style={{ marginRight: 7 }} />

      <Text style={styles.creatorRevealTitle}>
        {creatorRevealLoading ? "Opening preview..." : "Preview creator before booking?"}
      </Text>
    </TouchableOpacity>
  ) : null}

  {creatorRevealError ? (
    <Text style={styles.creatorRevealError}>{creatorRevealError}</Text>
  ) : null}
</View>
                      {/* Tag below image */}
                      {creator.ContentCategories ? (
                        <View style={styles.tagBox}>
                          <Text style={styles.tagText}>{creator.ContentCategories} Creator</Text>
                        </View>
                      ) : null}

                      {/* Social proof */}
                      {creator.VenueCount > 0 ? (
                        <View style={styles.acceptedBox}>
                          <Ionicons
                            name="checkmark-circle"
                            size={18}
                            color="green"
                            style={{ marginRight: 6 }}
                          />
                          <Text style={styles.acceptedText}>
                            <Text style={{ fontWeight: "700" }}>{creator.VenueCount}</Text>{" "}
                            other venues using collabbay {"\n"}have accepted this creator
                          </Text>
                        </View>
                      ) : null}

                      {/* Date + Countdown */}
                      <View style={styles.middleSection}>
                        <Text style={styles.visitSmall}>
                          For {booking?.BrandName || "your venue"} on
                        </Text>
                        <Text style={styles.visitDate}>{niceWhen}</Text>
                        {countdown ? <Text style={styles.countdownText}>{countdown}</Text> : null}
                      </View>

                      {/* Exchange info box */}
                      <View style={styles.infoBox}>
                        <Ionicons
                          name="film-outline"
                          size={18}
                          color="#000"
                          style={{ marginRight: 6 }}
                        />
                        <Text style={styles.infoText}>
                        For this creator to visit, promote {booking?.BrandName || "your venue"} and
                          deliver their reel within {turnaround} day
                          {turnaround === "1" ? "" : "s"}, you can accept this collab. If it is a proposal, not a request, the creator will also need to confirm the booking.
                        </Text>
                      </View>

                 {/* CTA */}
<TouchableOpacity
  style={styles.primaryButton}
  onPress={() => Linking.openURL("https://collabbayadmin.com/subscribe")}
  activeOpacity={0.9}
>
  <Text style={styles.primaryText}>Review or accept booking</Text>
  <Ionicons
    name="arrow-forward"
    size={18}
    color="#000"
    style={{ marginLeft: 8 }}
  />
</TouchableOpacity>

<View style={styles.secureCollabsBox}>
  <Ionicons name="shield-checkmark-outline" size={14} color="#666" />
  <Text style={styles.secureCollabsText}>Secure collabs with collabbay™</Text>
</View>

{CollabID ? <Text style={styles.collabRef}>Ref: Collab #{CollabID}</Text> : null}
                    </View>
                  </View>
                </View>

              </View>
            </ScrollView>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
};

// ---------- STYLES ----------
const IMG_BORDER = 34;

const styles = StyleSheet.create({
  // DESKTOP SHELL
  root: {
    flex: 1,
    backgroundColor: "#f3f4f6",
  },
  safeAreaShell: {
    flex: 1,
  },
  shellRow: {
    flex: 1,
    flexDirection: "row",
  },
  mainArea: {
    flex: 1,
    backgroundColor: "#f3f3f3",
  },
  scrollContent: {
    paddingVertical: 18,
    paddingHorizontal: 18,
    alignItems: "center",
    alignSelf: "center",
  },
  pageInner: {
    width: "100%",
    maxWidth: 1100,
    alignSelf: "center",
  },

  // LOADING / ERROR
  loadingCenter: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: "#555",
    fontSize: 14,
  },
  errorText: {
    color: "#111827",
    fontSize: 14,
  },

  // BACK
  backContainer: {
    width: "100%",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  backButton: {
    backgroundColor: "#fff",
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#eee",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  backButtonText: {
    marginLeft: 8,
    fontSize: 13,
    color: "#333",
    fontWeight: "600",
  },

  // CARD
  card: {
    width: "100%",
    borderRadius: 22,
    backgroundColor: "#fff",
    alignItems: "center",
    paddingVertical: 18,
    paddingHorizontal: 18,
    borderWidth: 1,
    borderColor: "#eee",
  },
  cardRow: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap", // if viewport is narrow, it will naturally stack
    justifyContent: "space-between",
  },
  leftCol: {
    width: "46%",
    minWidth: 320,
    paddingRight: 12,
  },
  rightCol: {
    width: "54%",
    minWidth: 320,
    paddingLeft: 12,
  },

  // IMAGE
  imageWrapper: {
    position: "relative",
    width: "100%",
    borderRadius: IMG_BORDER,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#f1f1f1",
  },
  profileImage: {
    width: "100%",
    height: 360,
    resizeMode: "cover",
    borderRadius: IMG_BORDER,
  },
  imageFallback: {
    width: "100%",
    height: 360,
    borderRadius: IMG_BORDER,
    backgroundColor: "#111",
    justifyContent: "center",
    alignItems: "center",
  },
  imageFallbackText: {
    color: "#FDF05D",
    fontSize: 46,
    fontWeight: "900",
  },

  overlayLeft: { position: "absolute", bottom: 18, left: 18 },
  overlayRight: { position: "absolute", bottom: 18, right: 18 },
  overlayBox: {
    backgroundColor: "rgba(0,0,0,0.35)",
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  overlayBigText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 20,
  },
  overlaySmallText: {
    color: "#fff",
    fontSize: 11,
    opacity: 0.9,
  },

  // CONTENT (same as your mobile, just placed in right col)
  middleSection: { marginTop: 10, alignItems: "center" },
  username: {
    fontSize: 20,
    fontWeight: "500",
    color: "#000",
    marginBottom: 6,
    letterSpacing: 0.3,
    textAlign: "center",
  },
  blurredText: {
  color: "transparent",
  textShadowColor: "rgba(0,0,0,0.3)",
  textShadowRadius: 6,
},



creatorNameRow: {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 4,
},

verifiedTick: {
  marginLeft: 6,
  marginBottom: 5,
},

creatorRevealButton: {
  marginTop: 4,
  marginBottom: 8,
  backgroundColor: "#1DA1F2",
  borderRadius: 999,
  paddingVertical: 8,
  paddingHorizontal: 13,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center",
  borderWidth: 1,
  borderColor: "#e5e5e5",
},

creatorRevealTitle: {
  color: "#fff",
  fontSize: 13,
  fontWeight: "700",
},

creatorRevealError: {
  marginTop: 7,
  fontSize: 12,
  color: "#777",
  textAlign: "center",
  fontWeight: "500",
},
  tagBox: {
    marginTop: 6,
    backgroundColor: "#f3f3f3",
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 20,
    alignSelf: "center",
  },
  tagText: { fontSize: 14, color: "#000", fontWeight: "600" },

  acceptedBox: {
    marginTop: 10,
    backgroundColor: "#fafafa",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    maxWidth: 520,
  },
  acceptedText: {
    fontSize: 14,
    color: "#000",
    textAlign: "center",
    lineHeight: 18,
    flexShrink: 1,
  },

  visitSmall: {
    color: "#777",
    fontSize: 15,
    marginTop: 4,
    textAlign: "center",
  },
  visitDate: {
    fontSize: 26,
    fontWeight: "800",
    color: "#000",
    textAlign: "center",
    marginTop: 6,
  },
  countdownText: {
    marginTop: 4,
    fontSize: 15,
    fontWeight: "500",
    color: "#777",
    textAlign: "center",
  },

  infoBox: {
    marginTop: 14,
    backgroundColor: "#f9f9f9",
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    width: "100%",
    maxWidth: 620,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
  },
  infoText: {
    color: "#000",
    fontSize: 14,
    textAlign: "center",
    flexShrink: 1,
    lineHeight: 20,
  },

  primaryButton: {
    width: "100%",
    maxWidth: 620,
    backgroundColor: "#FDF05D",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    marginTop: 16,
    alignSelf: "center",
  },
primaryText: {
  color: "#000",
  fontWeight: "900",
  fontSize: 16,
},
secureCollabsBox: {
  marginTop: 10,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
},
secureCollabsText: {
  marginLeft: 5,
  color: "#666",
  fontSize: 12,
  fontWeight: "600",
},
collabRef: {
  color: "#aaa",
  fontSize: 12,
  marginTop: 8,
  textAlign: "center",
},
});

// ---------------- SIDE NAV STYLES (same look as BrandCalendar) ----------------
const snav = StyleSheet.create({
  sideNav: {
    width: 250,
    backgroundColor: "#000",
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: "hidden",
    position: "relative",
  },
  sideNavGlow: {
    position: "absolute",
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: "#f5f5f5",
    fontWeight: "600",
    letterSpacing: 0.3,
  },
  logoDot: {
    color: "#FDF05D",
    fontSize: 18,
    fontWeight: "800",
  },
  logoDesktopTag: {
    fontSize: 9,
    color: "#b3b3b3",
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: "flex-start",
    maxWidth: "100%",
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: 1,
    textAlign: "left",
  },
  navItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navItemActive: {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderWidth: 1,
    borderColor: "rgba(253,240,93,0.18)",
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  navText: {
    color: "#f5f5f5",
    fontSize: 14,
    fontWeight: "500",
  },
  lockIcon: {
    marginLeft: 6,
  },
  peerMapCard: {
    borderRadius: 14,
    overflow: "hidden",
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
  },
  peerMapContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  peerMapTitle: {
    color: "#f5f5f5",
    fontSize: 13,
    fontWeight: "600",
  },
  peerMapSubtitle: {
    color: "#b3b3b3",
    fontSize: 11,
    marginTop: 2,
  },
  subscribeCard: {
    borderRadius: 14,
    overflow: "hidden",
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: "700",
    color: "#000",
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: "#1f2933",
    marginTop: 2,
  },
  sideNavBottom: {
    marginTop: "auto",
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#181818",
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: "#f5f5f5",
    fontWeight: "600",
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: "#b3b3b3",
    marginTop: 2,

},
});

export default CreatorPreview;
